import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

const TrackingContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TrackingProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const dataLayerRef = useRef([]);

  // Initialize dataLayer
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      dataLayerRef.current = window.dataLayer;
    }
  }, []);

  // Fetch tracking config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/config/tracking`);
        setConfig(response.data);
        
        if (response.data.tracking_enabled) {
          initializeTracking(response.data);
        }
      } catch (error) {
        console.error("Error fetching tracking config:", error);
      }
    };
    
    fetchConfig();
  }, []);

  const initializeTracking = (trackingConfig) => {
    if (initialized) return;
    
    // Load Meta Pixel
    if (trackingConfig.meta_pixel_id) {
      loadMetaPixel(trackingConfig.meta_pixel_id);
    }
    
    // Load Google Analytics
    if (trackingConfig.ga4_measurement_id) {
      loadGA4(trackingConfig.ga4_measurement_id);
    }
    
    // Load Google Tag Manager
    if (trackingConfig.gtm_container_id) {
      loadGTM(trackingConfig.gtm_container_id);
    }
    
    setInitialized(true);
  };

  const loadMetaPixel = (pixelId) => {
    if (typeof window === "undefined" || window.fbq) return;
    
    // Meta Pixel base code
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    
    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');
    
    if (config?.debug_mode) {
      console.log('[Tracking] Meta Pixel loaded:', pixelId);
    }
  };

  const loadGA4 = (measurementId) => {
    if (typeof window === "undefined") return;
    
    // Check if already loaded
    if (document.querySelector(`script[src*="${measurementId}"]`)) return;
    
    // Load gtag script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
    
    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag(){window.dataLayer.push(arguments);}
    window.gtag = gtag;
    gtag('js', new Date());
    gtag('config', measurementId);
    
    if (config?.debug_mode) {
      console.log('[Tracking] GA4 loaded:', measurementId);
    }
  };

  const loadGTM = (containerId) => {
    if (typeof window === "undefined") return;
    
    // Check if already loaded
    if (document.querySelector(`script[src*="${containerId}"]`)) return;
    
    // GTM script
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer',containerId);
    
    if (config?.debug_mode) {
      console.log('[Tracking] GTM loaded:', containerId);
    }
  };

  // Generate unique event ID for deduplication
  const generateEventId = () => {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Main tracking function
  const trackEvent = useCallback(async (eventName, eventData = {}) => {
    if (!config?.tracking_enabled) return;
    
    const eventId = generateEventId();
    const fullEventData = {
      event_name: eventName,
      event_id: eventId,
      ...eventData,
      page_url: typeof window !== "undefined" ? window.location.href : null,
      timestamp: new Date().toISOString()
    };
    
    if (config?.debug_mode) {
      console.log('[Tracking] Event:', eventName, fullEventData);
    }
    
    // Push to dataLayer (for GTM)
    if (typeof window !== "undefined" && window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...fullEventData
      });
    }
    
    // Send to Meta Pixel (client-side)
    if (typeof window !== "undefined" && window.fbq && config?.meta_pixel_id) {
      const metaEventMap = {
        "page_view": "PageView",
        "view_profile": "ViewContent",
        "lead": "Lead",
        "start_checkout": "InitiateCheckout",
        "purchase": "Purchase",
        "sign_up": "CompleteRegistration"
      };
      
      const metaEventName = metaEventMap[eventName] || eventName;
      const metaParams = {
        eventID: eventId  // For deduplication with CAPI
      };
      
      if (eventData.value) {
        metaParams.value = eventData.value;
        metaParams.currency = eventData.currency || "USD";
      }
      
      if (eventData.listener_id) {
        metaParams.content_ids = [eventData.listener_id];
        metaParams.content_type = "product";
      }
      
      window.fbq('track', metaEventName, metaParams, { eventID: eventId });
    }
    
    // Send to GA4 (client-side)
    if (typeof window !== "undefined" && window.gtag && config?.ga4_measurement_id) {
      const ga4Params = {
        ...eventData,
        send_to: config.ga4_measurement_id
      };
      window.gtag('event', eventName, ga4Params);
    }
    
    // Send to backend (for Meta CAPI)
    try {
      await axios.post(`${API}/tracking/event`, {
        event_name: eventName,
        event_id: eventId,
        ...eventData
      });
    } catch (error) {
      if (config?.debug_mode) {
        console.error('[Tracking] Backend error:', error);
      }
    }
  }, [config]);

  // Convenience methods for common events
  const trackPageView = useCallback((pageData = {}) => {
    trackEvent("page_view", pageData);
  }, [trackEvent]);

  const trackViewProfile = useCallback((listenerId, listenerName) => {
    trackEvent("view_profile", {
      listener_id: listenerId,
      listener_name: listenerName,
      content_type: "listener_profile"
    });
  }, [trackEvent]);

  const trackVideoPlay = useCallback((listenerId, videoProgress = 0) => {
    trackEvent("video_play", {
      listener_id: listenerId,
      video_progress: videoProgress
    });
  }, [trackEvent]);

  const trackVideoProgress = useCallback((listenerId, progressPercent) => {
    trackEvent("video_progress", {
      listener_id: listenerId,
      progress_percent: progressPercent
    });
  }, [trackEvent]);

  const trackClickCTA = useCallback((ctaName, listenerId = null) => {
    trackEvent("click_cta", {
      cta_name: ctaName,
      listener_id: listenerId
    });
  }, [trackEvent]);

  const trackStartCheckout = useCallback((listenerId, value, currency = "USD") => {
    trackEvent("start_checkout", {
      listener_id: listenerId,
      value,
      currency
    });
  }, [trackEvent]);

  const trackPurchase = useCallback((sessionId, listenerId, value, currency = "USD") => {
    trackEvent("purchase", {
      session_id: sessionId,
      listener_id: listenerId,
      value,
      currency,
      transaction_id: sessionId
    });
  }, [trackEvent]);

  const trackSignUp = useCallback((userId, method = "email") => {
    trackEvent("sign_up", {
      user_id: userId,
      method
    });
  }, [trackEvent]);

  const trackLead = useCallback((listenerId = null) => {
    trackEvent("lead", {
      listener_id: listenerId
    });
  }, [trackEvent]);

  return (
    <TrackingContext.Provider value={{
      config,
      initialized,
      trackEvent,
      trackPageView,
      trackViewProfile,
      trackVideoPlay,
      trackVideoProgress,
      trackClickCTA,
      trackStartCheckout,
      trackPurchase,
      trackSignUp,
      trackLead
    }}>
      {children}
    </TrackingContext.Provider>
  );
};

export const useTracking = () => {
  const context = useContext(TrackingContext);
  if (!context) {
    // Return no-op functions if not in provider
    return {
      config: null,
      initialized: false,
      trackEvent: () => {},
      trackPageView: () => {},
      trackViewProfile: () => {},
      trackVideoPlay: () => {},
      trackVideoProgress: () => {},
      trackClickCTA: () => {},
      trackStartCheckout: () => {},
      trackPurchase: () => {},
      trackSignUp: () => {},
      trackLead: () => {}
    };
  }
  return context;
};
